import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Certificates } from "../components/Certificates";
import { DegreeDocuments } from "../components/DegreeDocuments";
import { Header } from "../components/Header";
import { TagPill } from "../components/TagPill";
import { contacts, cv } from "../data/profile";
import { listProjects } from "../lib/posts";
import "./curriculum.css";

const cvContacts = contacts.filter((contact) => ["Direct email", "LinkedIn", "GitHub"].includes(contact.label));

export function CurriculumPage() {
  // Share the portfolio query: all published projects join the CV automatically.
  // listProjects only calls our backend; database access remains server-side.
  const projectsQuery = useQuery({ queryKey: ["projects"], queryFn: listProjects });
  const projects = (projectsQuery.data ?? []).filter((project) => project.status === "published");
  const projectSlugs = new Set(projects.map((project) => project.slug));
  const historicalProjects = cv.historicalProjects.filter((project) => !project.siteSlug || !projectSlugs.has(project.siteSlug));

  return (
    <>
      <Header />
      <main className="cv-shell verified-cv" id="conteudo">
        <nav className="cv-jump-nav" aria-label="Resume sections">
          <a href="#experiencia">Experience</a>
          <a href="#formacao">Education</a>
          <a href="#degree-documents">Degree</a>
          <a href="#certificates">Certificates</a>
          <a href="#projetos-cv">Projects</a>
          <a href="#competencias">Skills</a>
        </nav>
        <article className="cv-document" aria-label="Nuno Miranda's resume">
          <header className="cv-hero">
            <div className="cv-identity"><div className="cv-photo-ring"><img className="cv-photo" src="/nuno-avatar-ai.png" alt="Nuno Miranda" width={160} height={160} fetchPriority="high" /></div><div className="cv-identity-copy">
              <span>Resume</span>
              <h1>{cv.name}</h1>
              <p>{cv.role}</p>
              <small>{cv.location} · Web, APIs & interactive experiences</small>
              <a className="cv-hero-email" href="mailto:nuno_miranda@outlook.com">nuno_miranda@outlook.com ↗</a>
            </div></div>
            <div className="cv-export">
              <a className="print-button" href="/api/resume-pdf" download="Nuno-Miranda-Resume-and-Certificates.pdf">Download as PDF</a>
              <small>Includes the full resume and available original certificates.</small>
            </div>
          </header>

          <section className="cv-section intro-section" aria-labelledby="perfil-titulo">
            <h2 id="perfil-titulo">Profile</h2>
            <p>{cv.summary}</p>
            <div className="cv-highlight-grid">
              {cv.highlights.map((item) => <article key={item}>{item}</article>)}
            </div>
          </section>

          <section className="cv-section timeline-section" id="experiencia" aria-labelledby="experiencia-titulo">
            <h2 id="experiencia-titulo">Professional experience</h2>
            <div className="timeline-list cv-career-list">
              {cv.experience.map((item) => (
                <article key={item.organization}>
                  <span>{item.period}</span>
                  <div>
                    <h3>{item.title}</h3>
                    <p className="cv-organization">{item.organization} <span>· {item.sector}</span></p>
                    <p>{item.description}</p>
                    {item.contributions.length > 0 ? (
                      <ul className="cv-contributions">
                        {item.contributions.map((contribution) => <li key={contribution}>{contribution}</li>)}
                      </ul>
                    ) : null}
                  </div>
                </article>
              ))}
            </div>
          </section>

          <section className="cv-section" id="formacao" aria-labelledby="formacao-titulo">
            <h2 id="formacao-titulo">Education</h2>
            <div className="timeline-list cv-education-list">
              {cv.education.map((item) => (
                <article key={item.institution}>
                  <span>{item.period}</span>
                  <div>
                    <h3>{item.title}</h3>
                    <p className="cv-organization">{item.institution}</p>
                    <p>{item.detail}</p>
                  </div>
                </article>
              ))}
            </div>

          </section>

          <DegreeDocuments />
          <Certificates />

          <section className="cv-section" id="projetos-cv" aria-labelledby="projetos-titulo">
            <div className="cv-section-heading">
              <h2 id="projetos-titulo">Projects</h2>
              <Link className="cv-portfolio-link" to="/projects">Explore portfolio <span aria-hidden="true">↗</span></Link>
            </div>
            <p>Client applications, development experiments and personal projects.</p>
            <div className="cv-project-list">
              {historicalProjects.map((project) => (
                <article className="cv-project" key={project.title}>
                  <div className="cv-project-meta"><span>{project.period}</span><span>{project.kind}</span></div>
                  <h3>{project.title}</h3>
                  <p>{project.summary}</p>
                  <div className="cv-project-technologies">{project.technologies.map((technology) => <span key={technology}>{technology}</span>)}</div>
                </article>
              ))}
              {projectsQuery.isPending ? <p role="status">Loading portfolio projects…</p> : null}
              {projects.map((project) => {
                const original = cv.historicalProjects.find((entry) => entry.siteSlug === project.slug);
                return (
                  <article className="cv-project" key={project.id}>
                    <div className="cv-project-meta"><span>{original?.period ?? "Portfolio"}</span><span>{original?.kind ?? "Published project"}</span></div>
                    <h3><Link to="/projects/$slug" params={{ slug: project.slug }}>{project.title}<span aria-hidden="true">↗</span></Link></h3>
                    <p>{project.summary}</p>
                    <div className="cv-project-technologies">
                      {project.tags?.map((tag) => <TagPill key={tag.id} tag={tag} />)}
                    </div>
                  </article>
                );
              })}
            </div>
          </section>

          <section className="cv-section" id="competencias" aria-labelledby="competencias-titulo">
            <h2 id="competencias-titulo">Technical skills</h2>
            <div className="skill-cloud">{cv.skills.map((skill) => <span key={skill}>{skill}</span>)}</div>
          </section>

          <section className="cv-section cv-contact-strip" aria-labelledby="contactos-titulo">
            <h2 id="contactos-titulo">Get in touch</h2>
            <div>
              {cvContacts.map((contact) => (
                <a href={contact.href} key={contact.href} rel={contact.href.startsWith("https:") ? "noreferrer" : undefined} target={contact.href.startsWith("https:") ? "_blank" : undefined}>
                  <span>{contact.label}</span><strong>{contact.handle}</strong>
                </a>
              ))}
            </div>
          </section>
        </article>
      </main>
    </>
  );
}
